import type { RotaPatternResponse } from '@lmc/ts-common'
import { getJSON } from 'common/services/api'
import { useMemo, useState } from 'react'
import { useQuery } from 'react-query'
import { getQueryParams } from 'utils/getQueryParams'
import type { SortObject } from '../components/RotaTable'
import { SHIFT, VISIT } from '../constants'
import type { RotaFiltersValue } from '../RotaBuilder/common/components/RotaFilters'
import type { RotaPatternModalType } from '../RotaPattern/utils'
import { ROTA_PATTERN_QUERY_KEY, ROTA_PATTERN_URL } from './queryConstants'

type UseRotaPatternsListProps = {
  type?: RotaPatternModalType
  patternListFilters?: RotaFiltersValue
  patternListSorting?: SortObject
}

type UseRotaPatternsListReturn = {
  patternList?: RotaPatternResponse[]
  isLoadingList?: boolean
  refetchList?: () => void

  pagination: Pagination
  setPage: (page: PageProps) => void
}

export type Pagination = {
  currentPage: number
  totalItems: number
  totalPages?: number
  itemsPerPage: number
}

export type PageProps = { index: number; size: number }

export const PAGE_SIZE = 10
const initialPage = { index: 1, size: PAGE_SIZE }

export const useRotaPatternsList = ({
  type,
  patternListFilters,
  patternListSorting
}: UseRotaPatternsListProps): UseRotaPatternsListReturn => {
  const [page, setPage] = useState<PageProps>(initialPage)

  // LIST
  const listParams = getQueryParams({
    ...patternListFilters,
    ...{
      sortField: patternListSorting?.field,
      sortOrder: patternListSorting?.order === 'ascend' ? 'asc' : 'desc'
    },
    ...{ page: page.index, limit: page.size },
    siteShift: type === SHIFT,
    ...(type === VISIT && {
      fields: ['*', 'resident.name', 'resident.picture', 'resident.status']
    })
  })

  const listQueryKeys = useMemo(
    () =>
      [
        ROTA_PATTERN_QUERY_KEY,
        'list',
        type,
        page.index,
        page.size,
        patternListSorting?.field,
        patternListSorting?.order,
        patternListFilters?.endDate,
        patternListFilters?.flexiType,
        patternListFilters?.frequencies,
        patternListFilters?.skills,
        patternListFilters?.startDate,
        patternListFilters?.subjectIds,
        // patternListFilters?.teamMembersNeeded,
        patternListFilters?.instanceTypeIds,
        patternListFilters?.showInactive
      ].filter((item) => item !== undefined),
    [ROTA_PATTERN_QUERY_KEY, page, type, patternListSorting, patternListFilters]
  )

  const {
    data: patternListData,
    isLoading: isLoadingList,
    refetch: refetchList
  } = useQuery(
    listQueryKeys as string[],
    () =>
      getJSON({
        url: `${ROTA_PATTERN_URL}?${listParams}`,
        isRota: true
      }),
    { enabled: true }
  )

  return {
    patternList: patternListData?.data,
    isLoadingList,
    refetchList,
    pagination: patternListData?.pagination,
    setPage
  }
}
